/**
 * LensAnnotations.js — Lens V2 Annotation Contract
 *
 * Standard contract for Lens-readable surfaces.
 * Every inspectable element must expose structured metadata via data attributes.
 *
 * Usage:
 *   <div {...lensAnnotation({
 *     id: 'misuse-future-card-vendor',
 *     type: 'misuse-future',
 *     title: 'Vendor Payment Escape',
 *     meaning: 'Shows how vendor payments can still escape validation',
 *     actions: ['Apply suggested Pack', 'View related Twins'],
 *     relations: ['payment-twins', 'granullo-pass'],
 *     state: 'active-risk',
 *     priority: 'high',
 *   })}>
 */

// Standard data attribute keys
export const LENS_ATTRS = {
  ID: 'data-lens-id',
  TYPE: 'data-lens-type',
  TITLE: 'data-lens-title',
  MEANING: 'data-lens-meaning',
  ACTIONS: 'data-lens-actions',
  RELATIONS: 'data-lens-relations',
  STATE: 'data-lens-state',
  PRIORITY: 'data-lens-priority',
};

// Standard Lens node types
export const LENS_TYPES = {
  PROGRAM: 'program',
  RULE_PACK: 'rule-pack',
  PRESSURE_CARD: 'pressure-card',
  MISUSE_FUTURE: 'misuse-future',
  STRUCTURAL_FIX: 'structural-fix',
  PAYMENT_TWIN: 'payment-twin',
  GRANULLO_PASS: 'granullo-pass',
  CONSEQUENCE_MEMORY: 'consequence-memory',
  BENEFICIARY_OUTCOME: 'beneficiary-outcome',
  SCENARIO_CARD: 'scenario-card',
  TIMELINE_EVENT: 'timeline-event',
  TRUST_STATE: 'trust-state',
  NAV_ITEM: 'nav-item',
  CTA: 'cta',
  METRIC: 'metric',
  SECTION: 'section',
  TOPOLOGY_NODE: 'topology-node',
  RELATIONSHIP_LINE: 'relationship-line',
  // Play OS — stakeholder conviction surfaces
  PLAY_ACT: 'play-act',
  PLAY_ARTIFACT: 'play-artifact',
  PLAY_EVIDENCE: 'play-evidence',
  PLAY_POLICY: 'play-policy',
  PLAY_VISUALIZATION: 'play-visualization',
  PLAY_HERO: 'play-hero',
  PLAY_CTA: 'play-cta',
};

// Relationship map for the Granullo truth chain
export const TRUTH_CHAIN = [
  { from: 'program', to: 'rule-pack', label: 'defines' },
  { from: 'rule-pack', to: 'pressure-card', label: 'monitors via' },
  { from: 'pressure-card', to: 'misuse-future', label: 'exposes' },
  { from: 'misuse-future', to: 'structural-fix', label: 'resolved by' },
  { from: 'structural-fix', to: 'payment-twin', label: 'emits' },
  { from: 'payment-twin', to: 'granullo-pass', label: 'updates' },
  { from: 'granullo-pass', to: 'consequence-memory', label: 'records' },
  { from: 'consequence-memory', to: 'beneficiary-outcome', label: 'determines' },
];

/**
 * Build data-lens-* attributes for a component.
 * Returns an object of data attributes to spread onto a JSX element.
 */
export function lensAnnotation({ id, type, title, meaning, actions, relations, state, priority }) {
  const attrs = {};
  if (id) attrs[LENS_ATTRS.ID] = id;
  if (type) attrs[LENS_ATTRS.TYPE] = type;
  if (title) attrs[LENS_ATTRS.TITLE] = title;
  if (meaning) attrs[LENS_ATTRS.MEANING] = meaning;
  if (actions) attrs[LENS_ATTRS.ACTIONS] = Array.isArray(actions) ? actions.join('|') : actions;
  if (relations) attrs[LENS_ATTRS.RELATIONS] = Array.isArray(relations) ? relations.join('|') : relations;
  if (state) attrs[LENS_ATTRS.STATE] = state;
  if (priority) attrs[LENS_ATTRS.PRIORITY] = priority;
  return attrs;
}

/**
 * Parse lens annotation from a DOM element.
 */
export function parseLensAnnotation(element) {
  if (!element) return null;
  const id = element.getAttribute(LENS_ATTRS.ID);
  if (!id) return null;
  return {
    id,
    type: element.getAttribute(LENS_ATTRS.TYPE) || 'unknown',
    title: element.getAttribute(LENS_ATTRS.TITLE) || 'System component',
    meaning: element.getAttribute(LENS_ATTRS.MEANING) || '',
    actions: (element.getAttribute(LENS_ATTRS.ACTIONS) || '').split('|').filter(Boolean),
    relations: (element.getAttribute(LENS_ATTRS.RELATIONS) || '').split('|').filter(Boolean),
    state: element.getAttribute(LENS_ATTRS.STATE) || '',
    priority: element.getAttribute(LENS_ATTRS.PRIORITY) || 'normal',
    element,
  };
}

/**
 * Walk up from a clicked element to find the nearest Lens-annotated ancestor.
 */
export function findNearestAnnotation(element) {
  let current = element;
  while (current && current !== document.body) {
    if (current.getAttribute(LENS_ATTRS.ID)) {
      return parseLensAnnotation(current);
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Get related nodes from the truth chain for a given type.
 */
export function getRelatedNodes(type) {
  const upstream = TRUTH_CHAIN.filter(r => r.to === type).map(r => ({ type: r.from, label: r.label, direction: 'upstream' }));
  const downstream = TRUTH_CHAIN.filter(r => r.from === type).map(r => ({ type: r.to, label: r.label, direction: 'downstream' }));
  return [...upstream, ...downstream];
}

/**
 * Build the Lens response framework for an annotation.
 */
export function buildLensResponse(annotation) {
  if (!annotation) return null;
  const relations = getRelatedNodes(annotation.type);
  return {
    whatThisIs: annotation.title,
    whyItMatters: annotation.meaning,
    whatItMeansHere: annotation.state ? `Current state: ${annotation.state}` : '',
    whatYouCanDo: annotation.actions,
    whatItRelatesTo: [
      ...annotation.relations.map(r => ({ type: r, label: r })),
      ...relations,
    ],
    whatHappensNext: relations.filter(r => r.direction === 'downstream').map(r => r.type),
  };
}
